import ApiClient from '@/util/ApiClient'
import { defineStore } from 'pinia'
import { uuid } from 'vue-uuid'
import DuplicateElementRequest from '../models/DuplicateElementRequest'
import DuplicateElementResponse from '../models/DuplicateElementResponse'
import Element, { ElementType, RootEntityType } from '../models/Element'
import Resource from '../models/Resource'
import Table from '../models/Table'
import TableColumn from '../models/TableColumn'
import TableRow from '../models/TableRow'
import ElementReversibleAction from '../models/ElementReversibleAction'
import { useElementHistoryStore } from './ElementHistoryStore'
import DuplicateElementsRequest from '../models/DuplicateElementsRequest'
import ElementComponent from '@/modules/elements/components/ElementComponent.vue'
import { FocusPosition } from '@/util/reorder-util'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import {
  trackDuplicateElement,
  trackDuplicateElements,
  trackElementCreated,
  trackElementRemoved,
  trackElementsRemoved
} from '@/util/segment-tracking'
import InsertElementsRequest from '../models/InsertElementsRequest'
import { clearPendingContactCardUserIds } from '@/modules/elements/util/contactCardUsers'
import { markCanvas } from '@/util/canvas-load-timing'
import Vue from 'vue'
import {
  diffElementFields,
  ElementFieldChanges,
  ElementSaveMode,
  ElementSaveOptions,
  keepPlace,
  saveElementField,
  saveElementFields
} from './fieldSaves'
import {
  cancelUnsavedCreate,
  enqueueSave,
  forgetCreatesWaitingOn,
  isSaveQueueHeld,
  onSaveQueueBlocked,
  resumeCreatesWaitingOn,
  SaveParkedError,
  SaveWrite,
  useSaveCoordinatorStore,
  withdrawQueuedWrite
} from './SaveCoordinatorStore'
import { doQueuedAction } from './queuedActions'

export type BeforeDeleteCallback = (() => Promise<void>) | undefined

/**
 * How long a delete waits for the saves its callbacks flush. A save on a bad network is retried for much longer
 * (see SaveCoordinatorStore RETRY_DELAYS_MS); its change is already in the store, so the delete and its undo
 * snapshot are correct without waiting, and the save stays queued.
 */
export const BEFORE_DELETE_FLUSH_TIMEOUT_MS = 5000

/**
 * Runs the registered before-delete callbacks (they flush pending saves). A failing callback
 * is logged and never blocks the delete, and the delete waits at most BEFORE_DELETE_FLUSH_TIMEOUT_MS.
 */
export async function runBeforeDeleteCallbacks(callbacks?: BeforeDeleteCallback[]): Promise<void> {
  const snapshot = (callbacks || []).filter((callback): callback is () => Promise<void> => !!callback)
  if (!snapshot.length) return
  let timer: number | undefined
  const timeout = new Promise<void>(resolve => {
    timer = window.setTimeout(resolve, BEFORE_DELETE_FLUSH_TIMEOUT_MS)
  })
  const flushed = Promise.all(
    snapshot.map(async callback => {
      try {
        await callback()
      } catch (error) {
        console.warn('[ElementStore] before-delete callback failed', error)
      }
    })
  )
  await Promise.race([flushed, timeout])
  window.clearTimeout(timer)
}

/** Writes an element's place in the store in place, leaving its content alone. */
function writePlace(element: Element | undefined, position: number, parentId: string | undefined) {
  if (!element) return
  Vue.set(element, 'position', position)
  Vue.set(element, 'parentId', parentId)
}

/**
 * One request for several elements, queued on each element's queue as a barrier: it is sent once every element's
 * earlier writes are done (a block still being created is created first), and anything queued for an element later
 * (an undo's re-create) waits for it. An element whose entry is dropped (its unsent create was cancelled) no longer
 * holds the barrier. Nor does one whose queue starts waiting for Retry (its create parked, or its parent's) before
 * the request is sent: its entry is withdrawn, `onWithdraw` runs for it, and `send` gets the ids still in the batch.
 */
function enqueueBatch<R>(
  elementIds: string[],
  label: string,
  send: (ids: string[]) => Promise<R>,
  writeFor: (elementId: string) => Partial<SaveWrite<R>>,
  onWithdraw?: (elementId: string) => void
): Promise<(R | undefined)[]> {
  let request: Promise<R> | undefined
  let release: () => void = () => {}
  const allArrived = new Promise<void>(resolve => (release = resolve))
  // Only members arrive, and a member is withdrawn only before it arrives: `arrived` stays a subset of `members`
  const members = new Set(elementIds)
  const arrived = new Set<string>()
  const releaseIfAllArrived = () => {
    if (arrived.size === members.size) release()
  }
  const arrive = (elementId: string) => {
    if (!members.has(elementId)) return
    arrived.add(elementId)
    releaseIfAllArrived()
  }
  return Promise.all(
    elementIds.map(elementId => {
      const write: SaveWrite<R> = {
        elementId,
        kind: 'structural',
        idempotent: false,
        label,
        send: async () => {
          arrive(elementId)
          await allArrived
          return (request ??= send(elementIds.filter(id => members.has(id))))
        },
        ...writeFor(elementId)
      }
      const saved = enqueueSave<R>(write)
      // Its queue waits for Retry, possibly forever: the rest of the batch must not wait on it
      const stopWatching = onSaveQueueBlocked(elementId, () => {
        if (request || !withdrawQueuedWrite(elementId, write)) return
        members.delete(elementId)
        onWithdraw?.(elementId)
        releaseIfAllArrived()
      })
      const done = () => {
        stopWatching()
        arrive(elementId)
      }
      saved.then(done, done)
      return saved
    })
  )
}

/** The parent create parked for Retry: a child must not sit in-flight on a promise that only Retry or delete settles. */
export class ParentCreateParked extends Error {
  constructor() {
    super('The parent block is waiting for a retry')
    this.name = 'ParentCreateParked'
  }
}

type CreateGate = {
  promise: Promise<void>
  resolve: () => void
  reject: (error: unknown) => void
  /** Set when the create parks: new children fail the wait immediately instead of hanging inside send. */
  parked: boolean
}

/** Resolves once a block whose create is queued exists on the server (a child block is created after it). */
const blocksCreated = new Map<string, CreateGate>()

function trackCreate(elementId: string) {
  if (blocksCreated.has(elementId)) return
  let resolve: () => void = () => {}
  let reject: (error: unknown) => void = () => {}
  const promise = new Promise<void>((done, fail) => {
    resolve = done
    reject = fail
  })
  promise.catch(() => {})
  blocksCreated.set(elementId, { promise, resolve, reject, parked: false })
}

/**
 * The latest queued delete of each element. A page fetch can return a block whose delete is still waiting behind its
 * earlier saves: the delete's patch removes it again, unless a later re-create (undo) superseded that delete.
 */
const pendingRemovals = new Map<string, symbol>()

function trackRemoval(elementId: string): symbol {
  const token = Symbol(elementId)
  pendingRemovals.set(elementId, token)
  return token
}

function isLatestRemoval(elementId: string, token: symbol): boolean {
  return pendingRemovals.get(elementId) === token
}

function settleRemoval(elementId: string, token: symbol) {
  if (isLatestRemoval(elementId, token)) pendingRemovals.delete(elementId)
}

/**
 * A queued delete's patch: removes the block a page fetch put back, unless an undo re-created it since. A plain
 * function, not a store action: the coordinator re-applies patches after every response.
 */
function removalPatch(elementId: string, token: symbol): () => void {
  return () => {
    if (!isLatestRemoval(elementId, token)) return
    const store = useElementStore()
    if (store.elementById(elementId) || !store.isDeletedElement(elementId)) store.deleteElementFromStore(elementId)
  }
}

function markCreated(elementId: string) {
  const gate = blocksCreated.get(elementId)
  blocksCreated.delete(elementId)
  gate?.resolve()
  resumeCreatesWaitingOn(elementId)
}

/**
 * A block deleted before the server confirmed its create. The blocks added inside it meanwhile wait on that create
 * and were never sent: they go with it, here and on the server (which deletes a container's children), and must
 * never be sent, or the server would keep blocks whose parent it never had. A child already waiting inside its send
 * leaves it when the gate rejects, and the coordinator drops a deleted block's create.
 */
function abandonCreate(elementId: string) {
  const gate = blocksCreated.get(elementId)
  if (!gate) return
  blocksCreated.delete(elementId)
  const store = useElementStore()
  store.elements
    .filter(child => child.parentId === elementId)
    .forEach(child => {
      cancelUnsavedCreate(child.id)
      store.deleteElementFromStore(child.id)
      abandonCreate(child.id)
    })
  forgetCreatesWaitingOn(elementId)
  gate.parked = true
  gate.reject(new ParentCreateParked())
}

/** A parked create will not resolve until Retry. Wake children so they leave the in-flight send and wait queued. */
export function noteCreateParked(elementId: string) {
  const gate = blocksCreated.get(elementId)
  if (!gate || gate.parked) return
  gate.parked = true
  gate.reject(new ParentCreateParked())
}

export type ElementCallbacks = {
  beforeDelete: BeforeDeleteCallback[]
}

export type ElementState = {
  elements: Element[]
  deletedElements: string[]
  toggleElementStates: {
    [elementId: string]: boolean
  }
  elementCallbacks: {
    [elementId: string]: ElementCallbacks
  }
  startIndex: number
  selectedElementsLength: number
}

export const useElementStore = defineStore('element', {
  state: (): ElementState => ({
    elements: [],
    deletedElements: [],
    toggleElementStates: {},
    elementCallbacks: {},
    startIndex: -1,
    selectedElementsLength: -1
  }),
  getters: {
    elementById() {
      return (elementId: string): Element | undefined => this.elements.find(element => element.id === elementId)
    },
    isDeletedElement() {
      return (elementId: string): boolean => this.deletedElements.includes(elementId)
    },
    getElementCallbacks() {
      return (elementId: string): ElementCallbacks | undefined => this.elementCallbacks[elementId]
    }
  },
  actions: {
    // REVERSIBLE ACTIONS
    // They go through doQueuedAction: the undo queue only starts each write, and the caller waits for the save.
    async createElement(
      element: Element,
      elementComponent?: ElementComponent,
      focus?: FocusPosition
    ): Promise<Element> {
      element.id = uuid.v4()
      return doQueuedAction(
        element.rootEntityId,
        'Create Element',
        () => this.createElement_Internal(element, elementComponent, focus),
        () => this.deleteElement_Internal(this.elementById(element.id) ?? element)
      )
    },
    async deleteElement(elementId: string): Promise<void> {
      const element = this.elementById(elementId)
      if (element) {
        trackElementRemoved(element.id, element.type, element.rootEntityId)
        await runBeforeDeleteCallbacks(this.getElementCallbacks(elementId)?.beforeDelete)
        // Read again after the flush: the flushed text is in the store, and undo must restore it
        const flushed = this.elementById(elementId) ?? element
        return doQueuedAction(
          flushed.rootEntityId,
          'Delete Element',
          () => this.deleteElement_Internal(flushed),
          () => this.createElement_Internal(flushed)
        )
      }
    },
    async deleteElements(elementIds: string[]): Promise<void> {
      const elementsToDelete = this.elements.filter(e => elementIds.includes(e.id))
      if (!elementsToDelete.length) return
      trackElementsRemoved(elementsToDelete)
      await runBeforeDeleteCallbacks(elementIds.flatMap(id => this.getElementCallbacks(id)?.beforeDelete || []))
      // Read again after the flush, so undo restores the saved content
      const flushedElements = elementsToDelete.map(e => this.elementById(e.id) ?? e)
      return doQueuedAction(
        flushedElements[0].rootEntityId,
        'Delete multiple Elements',
        () => this.deleteElements_Internal(flushedElements),
        () => Promise.all(flushedElements.map(e => this.createElement_Internal(e)))
      )
    },
    /**
     * Saves what changed between two copies of an element, field by field: undo puts back only those fields, never
     * text typed since. `mode: 'content'` for typed text (shown at once, retried); the default `change` stores the
     * change once the server accepts it and rejects on failure. Resolves `null` for a deleted element.
     */
    async updateElement(
      oldElement: Element,
      newElement: Element,
      options: ElementSaveOptions & { mode?: ElementSaveMode } = {}
    ): Promise<Element | null> {
      if (this.isDeletedElement(oldElement.id)) {
        return null
      }
      const forward = diffElementFields(oldElement, newElement)
      if (!Object.keys(forward).length) return this.elementById(oldElement.id) ?? null
      const backward = diffElementFields(newElement, oldElement)
      const save = async (changes: ElementFieldChanges, saveOptions: ElementSaveOptions) =>
        (await saveElementFields(
          this.elementById(oldElement.id) ?? newElement,
          changes,
          options.mode ?? 'change',
          saveOptions
        )) ?? null
      // Only the caller's own first try shows the caller's error; undo and redo are reported by the coordinator
      return doQueuedAction(
        oldElement.rootEntityId,
        'Update Element',
        replay => save(forward, replay ? {} : options),
        () => save(backward, {})
      )
    },
    async replaceElement(oldElement: Element, newElement: Element): Promise<Element | null> {
      if (this.isDeletedElement(oldElement.id)) {
        return null
      }
      newElement.id = uuid.v4()
      const replacement = JSON.parse(JSON.stringify(newElement)) as Element
      const original = JSON.parse(JSON.stringify(oldElement)) as Element
      trackElementCreated(newElement.id, newElement.type, newElement.rootEntityId)
      // Replace runs on the old block's queue and its undo on the new block's: chain them, so an undo pressed while
      // the replace is still queued or in flight goes after it
      let last: Promise<unknown> = Promise.resolve()
      const inOrder = <T>(run: () => Promise<T>): Promise<T> => {
        const next = last.catch(() => undefined).then(run)
        last = next
        return next
      }
      return doQueuedAction(
        oldElement.rootEntityId,
        'Replace Element',
        replay =>
          inOrder(() => this.replaceElement_Internal(oldElement, JSON.parse(JSON.stringify(replacement)), !replay)),
        () => inOrder(() => this.replaceElement_Internal(replacement, JSON.parse(JSON.stringify(original))))
      )
    },
    async duplicateElement(
      duplicateElement: DuplicateElementRequest,
      element: Element
    ): Promise<DuplicateElementResponse> {
      const reversibleAction = new ElementReversibleAction<DuplicateElementResponse, void>()
      reversibleAction.name = 'Duplicate Element'
      reversibleAction.action = async () => await this.duplicateElement_Internal(duplicateElement, element.id)
      // Undo only starts the delete: it must not hold the undo queue behind the copy's queued saves
      reversibleAction.reverseAction = async () => {
        this.deleteElement_Internal(reversibleAction.actionResult?.element).catch(() => undefined)
      }
      trackDuplicateElement(element.id, element.type, element.rootEntityId)
      // The server copies what it has saved: let the element's queued saves land first
      await useSaveCoordinatorStore().whenSettled([element.id])
      return useElementHistoryStore().doAction(element.rootEntityId, reversibleAction)
    },
    async duplicateElements(request: DuplicateElementsRequest): Promise<DuplicateElementResponse[]> {
      const elementsToDuplicate = this.elements.filter(e => request.ids.includes(e.id))
      const reversibleAction = new ElementReversibleAction<DuplicateElementResponse[], void>()
      reversibleAction.action = async () => await this.duplicateElements_Internal(request)
      reversibleAction.name = 'Duplicate multiple Element'
      reversibleAction.reverseAction = async () => {
        this.deleteElements_Internal(reversibleAction.actionResult?.map(r => r.element) || []).catch(() => undefined)
      }
      const rootEntityId = elementsToDuplicate[0].rootEntityId
      trackDuplicateElements(elementsToDuplicate)
      await useSaveCoordinatorStore().whenSettled(request.ids)
      return useElementHistoryStore().doAction(rootEntityId, reversibleAction)
    },
    async insertElements(request: InsertElementsRequest): Promise<DuplicateElementResponse[]> {
      const reversibleAction = new ElementReversibleAction<DuplicateElementResponse[], void>()
      reversibleAction.action = async () => await this.insertElements_Internal(request)
      reversibleAction.name = 'Insert multiple Elements'
      reversibleAction.reverseAction = async () => {
        this.deleteElements_Internal(reversibleAction.actionResult?.map(r => r.element) || []).catch(() => undefined)
      }
      await useSaveCoordinatorStore().whenSettled(request.elementIds)
      return useElementHistoryStore().doAction(request.targetRootEntityId, reversibleAction)
    },
    async setElementPosition(element: Element, newPosition: number, parentId?: string): Promise<Element> {
      const currentPosition = element.position
      const currentParentId = element.parentId
      return doQueuedAction(
        element.rootEntityId,
        'Set Element position',
        () => this.setElementPosition_Internal(element, newPosition, parentId),
        () => this.setElementPosition_Internal(element, currentPosition, currentParentId)
      )
    },
    async setElementPositions(elements: Element[], newPositions: number[], parentId?: string): Promise<void> {
      const currentPositions = elements.map(e => e.position)
      const currentParentId = elements[0].parentId
      return doQueuedAction(
        elements[0].rootEntityId,
        'Set multiple Element positions',
        () => this.setElementPositions_Internal(elements, newPositions, parentId),
        () => this.setElementPositions_Internal(elements, currentPositions, currentParentId)
      )
    },
    // API CALLS
    // Each queues its write before its first `await` (see doQueuedAction).
    /**
     * Shows the block at once and queues its create; writes typed into it queue behind the create. If the create
     * fails for good, the block stays with everything typed into it and the queue holds until Retry (the coordinator
     * reports it): resolves with the block either way. A create is resent only after the server showed it does not
     * have it, so a lost response never creates the block (or an offer's default rows) twice.
     */
    async createElement_Internal(
      element: Element,
      elementComponent?: ElementComponent,
      focus?: FocusPosition
    ): Promise<Element> {
      element.id = element.id || uuid.v1()
      element.inCreation = true
      if (element.type === ElementType.tasklist) {
        const untypedElement = element as any
        untypedElement.open = true
      }
      this.setElement(element)
      if (elementComponent) {
        elementComponent.$nextTick(() => {
          elementComponent.$nextTick(() => {
            elementComponent.$emit('focusNext', focus)
          })
        })
      }
      const elementId = element.id
      const parentId = element.parentId
      // An undone delete: its queued delete must no longer remove the block from a page fetch
      pendingRemovals.delete(elementId)
      trackCreate(elementId)
      const created = (serverElement: Element) => {
        serverElement.inCreation = false
        this.setElement(serverElement)
        markCreated(elementId)
      }
      try {
        const response = await enqueueSave<Element>({
          elementId,
          kind: 'structural',
          idempotent: true,
          // Writes typed into the block queue behind this one (one queue per element)
          holdsQueueOnPark: true,
          verifyBeforeResend: true,
          afterElementId: parentId,
          label: 'new block',
          // Until the server has it, a page reload that clears the page's elements must not lose the block
          patch: () => {
            if (!this.elementById(elementId) && !this.isDeletedElement(elementId)) this.setElement(element)
          },
          send: async () => {
            // A block inside a container that is still being created goes after it. A parked parent rejects
            // this wait; the coordinator requeues the child until the parent exists.
            const parentGate = parentId ? blocksCreated.get(parentId) : undefined
            if (parentGate?.parked) throw new ParentCreateParked()
            if (parentGate) await parentGate.promise
            // The block as it is when sent: on a retry it carries what was typed meanwhile
            return (await ApiClient.post<Element>(`/elements`, this.elementById(elementId) ?? element)).data
          },
          apply: created,
          verify: () => true,
          applyVerified: created
        })
        return this.elementById(elementId) ?? response ?? element
      } catch (error) {
        if (error instanceof SaveParkedError) return this.elementById(elementId) ?? element
        throw error
      }
    },
    async deleteElement_Internal(element: Element | undefined): Promise<void> {
      if (!element) return
      const elementId = element.id
      // Its create never reached the server: there is nothing to delete there
      const unsaved = cancelUnsavedCreate(elementId)
      this.deleteElementFromStore(elementId)
      abandonCreate(elementId)
      if (unsaved) return
      const removal = trackRemoval(elementId)
      try {
        await enqueueSave<void>({
          elementId,
          kind: 'structural',
          idempotent: false,
          succeedsOn404: true,
          label: 'block delete',
          // A page fetch before the delete is sent must not bring the block back
          patch: removalPatch(elementId, removal),
          send: async () => {
            await ApiClient.delete(`/elements/${elementId}`)
          },
          // The server kept it: show it again
          onFailure: () => this.setElement(element)
        })
      } finally {
        settleRemoval(elementId, removal)
      }
      useSaveCoordinatorStore().purgeElement(elementId)
      if (element.type === ElementType.signature) {
        await useSpaceStore().onSignatureElementUpdated(element)
      }
    },
    async deleteElements_Internal(elements: Element[]): Promise<void> {
      const saved = elements.filter(e => !cancelUnsavedCreate(e.id))
      this.deleteElementsFromStore(elements.map(e => e.id))
      elements.forEach(e => abandonCreate(e.id))
      if (saved.length) {
        const ids = saved.map(e => e.id)
        const removals = new Map(ids.map(id => [id, trackRemoval(id)]))
        try {
          await enqueueBatch<unknown>(
            ids,
            'block delete',
            batchIds => ApiClient.post(`/elements/delete`, batchIds),
            elementId => ({
              succeedsOn404: true,
              // A page fetch before the delete is sent must not bring the block back
              patch: removalPatch(elementId, removals.get(elementId)!),
              onFailure: () => {
                const element = saved.find(e => e.id === elementId)
                if (element) this.setElement(element)
              }
            })
          )
        } finally {
          removals.forEach((removal, id) => settleRemoval(id, removal))
        }
        ids.forEach(id => useSaveCoordinatorStore().purgeElement(id))
      }
      const signatureElement = saved.find(e => e.type === ElementType.signature)
      if (signatureElement) {
        await useSpaceStore().onSignatureElementUpdated(signatureElement)
      }
    },
    /**
     * Saves one field (a dotted path such as `message.message`) of `element` through the save coordinator: the
     * store holds the new value at once, and the PUT follows any write queued earlier for the element. Resolves
     * `undefined` when the element was deleted or the save failed; the coordinator reports a failure and keeps the
     * change for a retry.
     */
    async saveElementField_Internal(element: Element, path: string, value: unknown): Promise<Element | undefined> {
      return saveElementField(element, path, value)
    },
    /**
     * Replaces a block after its own queued saves. The new block has a client id, so a timeout is checked against
     * it. Rejects on failure; `callerShowsFailure` skips the coordinator's message (the caller shows its own).
     */
    async replaceElement_Internal(
      oldElement: Element,
      newElement: Element,
      callerShowsFailure = false
    ): Promise<Element | null> {
      const oldId = oldElement.id
      const replaced = (serverElement: Element) => {
        this.deleteElementFromStore(oldId)
        this.setElement(serverElement)
      }
      // A divider has no content to wait for: show it now, the save confirms it
      const optimistic = newElement.type === ElementType.divider && !!this.elementById(oldId)
      if (optimistic) {
        this.deleteElementFromStore(oldId)
        this.setElement(JSON.parse(JSON.stringify(newElement)))
      }
      let response: Element | undefined
      try {
        response = await enqueueSave<Element>({
          elementId: oldId,
          kind: 'structural',
          idempotent: false,
          reportsFailure: !callerShowsFailure,
          label: 'block replace',
          send: async () => (await ApiClient.post<Element>(`/elements/${oldId}/replace`, newElement)).data,
          apply: replaced,
          verify: () => true,
          verifyElementId: newElement.id,
          applyVerified: replaced
        })
      } catch (error) {
        if (optimistic && !(error instanceof SaveParkedError)) {
          this.deleteElementFromStore(newElement.id)
          this.setElement(oldElement)
        }
        throw error
      }
      useSaveCoordinatorStore().purgeElement(oldId)
      return response ?? this.elementById(newElement.id) ?? null
    },
    /** A task item ticked in a read-only message. Settles; the coordinator reports a failure. */
    async updateMessageElementTaskItem_Internal(
      message: Element,
      taskItemId: string,
      checked: boolean
    ): Promise<Element | undefined> {
      return enqueueSave<Element>({
        elementId: message.id,
        kind: 'content',
        target: `task-item:${taskItemId}`,
        idempotent: true,
        label: 'task item',
        send: async () =>
          (await ApiClient.put<Element>(`/messageelements/${message.id}/taskitems/${taskItemId}`, { checked })).data,
        apply: response => this.setElement(keepPlace(response))
      }).catch(() => undefined)
    },
    async duplicateElement_Internal(
      duplicateElement: DuplicateElementRequest,
      elementId: string
    ): Promise<DuplicateElementResponse> {
      const responseData = (await ApiClient.post(`/elements/${elementId}/duplicate`, duplicateElement)).data
      this.saveDuplicationElementAndChildren(responseData)
      return responseData
    },
    async duplicateElements_Internal(request: DuplicateElementsRequest): Promise<DuplicateElementResponse[]> {
      const responses = (await ApiClient.post<DuplicateElementResponse[]>(`/elements/duplicate`, request)).data
      responses.forEach(response => this.saveDuplicationElementAndChildren(response))
      return responses
    },
    async insertElements_Internal(request: InsertElementsRequest): Promise<DuplicateElementResponse[]> {
      const responses = (await ApiClient.post<DuplicateElementResponse[]>(`/elements/insert`, request)).data
      responses.forEach(response => this.saveDuplicationElementAndChildren(response))
      return responses
    },
    /**
     * Moves one element: its place changes in the store at once (and stays until the server answers), its content is
     * never touched. `element` may be a copy captured earlier (undo).
     */
    async setElementPosition_Internal(element: Element, newPosition: number, parentId?: string): Promise<Element> {
      const elementId = element.id
      await enqueueSave<Element>({
        elementId,
        kind: 'structural',
        idempotent: false,
        label: 'block move',
        patch: () => writePlace(this.elementById(elementId), newPosition, parentId),
        send: async () =>
          (
            await ApiClient.put<Element>(`/elements/${elementId}/position`, { position: newPosition, parentId })
          ).data,
        apply: response => writePlace(this.elementById(elementId), response.position, response.parentId),
        verify: serverElement =>
          serverElement.position === newPosition && (serverElement.parentId ?? null) === (parentId ?? null)
      })
      return this.elementById(elementId) ?? element
    },
    /**
     * Moves several elements in one request, after each element's earlier writes (a block still being created is
     * created first: the server maps positions by id). Only their places are taken from the response.
     */
    async setElementPositions_Internal(elements: Element[], newPositions: number[], parentId?: string): Promise<void> {
      // A block whose create is parked for Retry never arrives, and the rest of the batch would wait on it.
      // Its place is written locally: the create is resent from the store, so the retry carries the new position.
      const moving: { id: string; position: number }[] = []
      const parked: { id: string; position: number; parentId: string | undefined }[] = []
      elements.forEach((element, index) => {
        if (isSaveQueueHeld(element.id)) {
          const stored = this.elementById(element.id)
          parked.push({ id: element.id, position: stored?.position ?? element.position, parentId: stored?.parentId })
          writePlace(stored, newPositions[index], parentId)
          return
        }
        moving.push({ id: element.id, position: newPositions[index] })
      })
      if (!moving.length) return
      const positionOf = new Map(moving.map(item => [item.id, item.position]))
      try {
        await enqueueBatch<Element[]>(
          moving.map(item => item.id),
          'block move',
          async ids => {
            const positions = ids.map(id => positionOf.get(id))
            return (await ApiClient.put<Element[]>(`/elements/positions`, { ids, positions, parentId })).data
          },
          elementId => ({
            apply: updatedElements => {
              const updated = updatedElements.find(e => e.id === elementId)
              if (updated) writePlace(this.elementById(elementId), updated.position, updated.parentId)
            }
          }),
          // Its create parked after the move was queued: like a block parked before, it moves locally
          elementId => {
            const stored = this.elementById(elementId)
            parked.push({ id: elementId, position: stored?.position ?? 0, parentId: stored?.parentId })
            writePlace(stored, positionOf.get(elementId)!, parentId)
          }
        )
      } catch (error) {
        parked.forEach(item => writePlace(this.elementById(item.id), item.position, item.parentId))
        throw error
      }
    },
    /**
     * Stores elements fetched from the server. Changes the server does not have yet are put back on top, and blocks
     * still being created are kept (a page fetch clears the page first).
     */
    setFetchedElements(elements: Element[]) {
      this.setElements(elements)
      useSaveCoordinatorStore().reapplyPendingPatches()
    },
    async fetchAllSpacePageElements(spaceId: string, spacePageId: string): Promise<Element[]> {
      markCanvas('elements-request')
      const response = await ApiClient.get<Element[]>(`/spaces/${spaceId}/pages/${spacePageId}/elements`)
      markCanvas('elements-response')
      // The caller commits. A superseded page switch must not write this response over a newer page.
      return response.data
    },
    async fetchAllTaskElements(taskId: string): Promise<Element[]> {
      const elements = (await ApiClient.get<Element[]>(`/tasks/${taskId}/elements`)).data
      this.setFetchedElements(elements)
      return elements
    },
    async fetchAllTaskTemplateElements(taskTemplateId: string): Promise<Element[]> {
      const elements = (await ApiClient.get<Element[]>(`/task-templates/${taskTemplateId}/elements`)).data
      this.setFetchedElements(elements)
      return elements
    },
    async fetchAllSpacePageTemplateElements(templateId: string): Promise<Element[]> {
      // Other templates' blocks are off screen: left in the store they join every block's all-elements scans.
      // Before the request, so a slow response for an earlier template cannot clear the one now on screen.
      this.clearOtherTemplateElements(templateId)
      const elements = (await ApiClient.get<Element[]>(`/space-page-templates/${templateId}/elements`)).data
      this.setFetchedElements(elements)
      return elements
    },
    // Store actions
    // Every block registers on mount and unregisters on unmount: change only its own entry, never copy the whole map
    // (that was O(n²) over a page mount or switch). Only actions read the callbacks, so nothing re-renders on it.
    registerBeforeDeleteCallback(elementId: string, callback: BeforeDeleteCallback) {
      const elementCallbacks = this.elementCallbacks[elementId] || {}
      elementCallbacks.beforeDelete = [...(elementCallbacks.beforeDelete || []), callback]
      Vue.set(this.elementCallbacks, elementId, elementCallbacks)
    },
    unRegisterBeforeDeleteCallback(elementId: string, callback: BeforeDeleteCallback) {
      const elementCallbacks = this.elementCallbacks[elementId] || {}
      elementCallbacks.beforeDelete = (elementCallbacks.beforeDelete || []).filter(c => c !== callback)
      Vue.set(this.elementCallbacks, elementId, elementCallbacks)
    },
    /**
     * `setElement` for many elements with one array assignment instead of one copy per element (a page fetch was
     * O(n²)). Same result: the other elements keep their order, the given ones follow in the given order, each merged
     * over what the store held.
     */
    setElements(elements: Element[]) {
      if (!elements.length) return
      const stored = new Map((this.elements as Element[]).map(e => [e.id, e]))
      const incoming = new Map<string, Element>()
      elements.forEach(element => {
        prepareStoredElement(element)
        const previous = incoming.get(element.id) ?? stored.get(element.id)
        incoming.delete(element.id)
        incoming.set(element.id, (previous ? { ...previous, ...element } : { ...element }) as Element)
      })
      this.deletedElements = this.deletedElements.filter(id => !incoming.has(id))
      this.elements = [...(this.elements as Element[]).filter(e => !incoming.has(e.id)), ...incoming.values()]
    },
    setElement(element: Element) {
      this.deletedElements = this.deletedElements.filter(e => e !== element.id)
      prepareStoredElement(element)
      let oldElement = (this.elements as Element[]).find(p => p.id === element.id)
      if (!oldElement) {
        this.elements = [...this.elements, { ...element } as Element]
      } else {
        oldElement = { ...oldElement, ...element } as Element
        this.elements = [...(this.elements as Element[]).filter(p => p.id !== element.id), oldElement]
      }
    },
    /**
     * Applies a content-save response without replacing the elements array or the stored block object.
     * Content patches already update in place; preserving identity keeps unrelated canvas blocks from re-rendering.
     */
    mergeElementInPlace(element: Element) {
      this.deletedElements = this.deletedElements.filter(e => e !== element.id)
      prepareStoredElement(element)
      const stored = (this.elements as Element[]).find(candidate => candidate.id === element.id)
      if (!stored) {
        this.setElement(element)
        return
      }
      Object.entries(element).forEach(([key, value]) => Vue.set(stored, key, value))
    },
    saveDuplicationElementAndChildren(response: DuplicateElementResponse) {
      this.setElement(response.element)
      response.children.forEach(child => this.saveDuplicationElementAndChildren(child))
    },
    deleteElementFromStore(elementId: string) {
      this.deletedElements = [...this.deletedElements, elementId]
      this.elements = this.elements.filter(e => e.id !== elementId)
    },
    deleteElementsFromStore(elementIds: string[]) {
      elementIds.forEach(e => this.deleteElementFromStore(e))
    },
    setToggleElementState(elementId: string, state: boolean) {
      this.toggleElementStates = { ...this.toggleElementStates, [elementId]: state }
    },
    clearOtherTemplateElements(keepTemplateId: string) {
      const kept = (this.elements as Element[]).filter(
        e => e.rootEntityType !== RootEntityType.SPACE_PAGE_TEMPLATE || e.rootEntityId === keepTemplateId
      )
      // Nothing to drop: leave the array alone so nothing watching it re-runs
      if (kept.length !== this.elements.length) this.elements = kept
    },
    clearPageElements(pageId: string) {
      this.elements = (this.elements as Element[]).filter(e => e.rootEntityId !== pageId)
      clearPendingContactCardUserIds(pageId)
    },
    setStartIndex(startIndex: number) {
      this.startIndex = startIndex
    },
    setSelectedElementsLength(selectedElementsLength: number) {
      this.selectedElementsLength = selectedElementsLength
    }
  }
})

/** Sorts a fetched element's ordered children in place, as the store keeps them. */
function prepareStoredElement(element: Element) {
  if (element.type === ElementType.table) {
    sortColumnsAndRows(element.table!)
  } else if (element.type === ElementType.resourcelist) {
    sortResources(element)
  }
}

function sortColumnsAndRows(table: Table) {
  if (table) {
    table.columns = [...table.columns].sort(comparePositions)
    table.rows = [...table.rows].sort(comparePositions)
  }
}

function sortResources(element: Element) {
  if (element.resourceList?.resources) {
    element.resourceList!.resources = [...element.resourceList!.resources!!].sort(comparePositions)
  }
}

function comparePositions(a: TableColumn | TableRow | Resource, b: TableColumn | TableRow | Resource) {
  return a.position - b.position
}

export type ElementStore = ReturnType<typeof useElementStore>
